import React, { useMemo } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';

import { Badge, Typography } from '@zendesk/forethought-elements';
import {
  ArticleCardTranscriptComponent,
  ExecutionTranscriptComponent,
  GotoWorkflowTranscriptComponent,
  IntentTriggeredTranscriptComponent,
  RelatedArticlesTranscriptComponent,
  SolveABTestVariantAssignmentTranscriptComponent,
  TranscriptComponent,
} from '../ConversationTranscript/types';
import { getWorkflowBuilderUrl, openTab } from '../helpers';
import IntentBadge from '../IntentBadge';
import { AddKnowledgeMenuButton } from './AddKnowledgeMenuButton';
import VariantPill from 'src/components/dashboard-pages/solve-insights/common/VariantPill';
import {
  ABTestSample,
  ABTestVariantInfo,
  buildVariantByWorkflowId,
} from 'src/services/insights/types';

const mapTranscriptComponentToChatEvent = (
  transcriptComponent: TranscriptComponent,
  variantByWorkflowId: Map<string, ABTestVariantInfo>,
  t: TFunction<'intent-conversation-analytics'>,
) => {
  switch (transcriptComponent.message_type) {
    case 'intent_trigger': {
      const comp = transcriptComponent as IntentTriggeredTranscriptComponent;
      const variant = comp.intent_workflow_id
        ? variantByWorkflowId.get(comp.intent_workflow_id)
        : undefined;
      return (
        <IntentPredictionEventLine
          transcriptComponent={comp}
          variant={variant}
        />
      );
    }
    case 'csat_submission':
      return (
        <GenericEventLine message={t('chatEventsSection.csatSubmitted')} />
      );
    case 'related_articles':
      return (
        <ArticleLineFromRelatedArticles
          transcriptComponent={
            transcriptComponent as RelatedArticlesTranscriptComponent
          }
        />
      );
    case 'article_card':
      return (
        <ArticleLineFromArticleCard
          transcriptComponent={
            transcriptComponent as ArticleCardTranscriptComponent
          }
        />
      );
    case 'execution':
      return (
        <ExecutionEventLine
          transcriptComponent={
            transcriptComponent as ExecutionTranscriptComponent
          }
        />
      );
    case 'goto_workflow': {
      const comp = transcriptComponent as GotoWorkflowTranscriptComponent;
      const variant = variantByWorkflowId.get(comp.intent_workflow_id);
      return (
        <GotoWorkflowEventLine transcriptComponent={comp} variant={variant} />
      );
    }
    case 'solve_ab_test_variant_assignment': {
      const comp =
        transcriptComponent as SolveABTestVariantAssignmentTranscriptComponent;
      const variant = variantByWorkflowId.get(comp.intent_workflow_id);
      return (
        <ABTestVariantAssignmentEventLine
          transcriptComponent={comp}
          variant={variant}
        />
      );
    }
    case 'sentiment_analysis':
      return <SentimentAnalysisEventLine />;
    default:
      return null;
  }
};

export const ChatEventsSection = ({
  abTestSamples,
  onCreateNewInstruction,
  transcriptComponents,
}: {
  abTestSamples?: ABTestSample[] | null;
  onCreateNewInstruction: (newId: string) => void;
  transcriptComponents: TranscriptComponent[];
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  const variantByWorkflowId = useMemo(() => {
    const map = abTestSamples
      ? buildVariantByWorkflowId(abTestSamples)
      : new Map<string, ABTestVariantInfo>();
    // Also extract from solve_ab_test_variant_assignment transcript components
    for (const comp of transcriptComponents) {
      if (comp.message_type === 'solve_ab_test_variant_assignment') {
        const abComp = comp as SolveABTestVariantAssignmentTranscriptComponent;
        if (!map.has(abComp.intent_workflow_id)) {
          map.set(abComp.intent_workflow_id, {
            ab_test_config_id: abComp.ab_test_config_id,
            ab_test_name: abComp.ab_test_name,
            ab_test_status: 'running',
            end_condition_type: null,
            end_date: null,
            intent_workflow_version: abComp.intent_workflow_version,
            is_control: abComp.is_control,
            sample_count: null,
            start_date: null,
            variant_name: abComp.variant_name,
            version_type: abComp.version_type,
          });
        }
      }
    }
    return map;
  }, [abTestSamples, transcriptComponents]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography color={palette.colors.grey[600]} variant='font12'>
          {t('chatEventsSection.events')}
        </Typography>
        <AddKnowledgeMenuButton onSuccess={onCreateNewInstruction} />
      </Box>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <VerticalLine />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {transcriptComponents.map((transcriptComponent, index) => (
            <React.Fragment key={`${transcriptComponent.timestamp}-${index}`}>
              {mapTranscriptComponentToChatEvent(
                transcriptComponent,
                variantByWorkflowId,
                t,
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const IntentPredictionEventLine = ({
  transcriptComponent,
  variant,
}: {
  transcriptComponent: IntentTriggeredTranscriptComponent;
  variant?: ABTestVariantInfo;
}) => {
  return (
    <LineWrapper>
      <Circle />
      <IntentBadge
        intentDefinitionId={transcriptComponent.intent_id}
        intentName={transcriptComponent.intent_title}
        intentNameSuffix={
          variant && (
            <ClickableVariantPill
              intentWorkflowId={transcriptComponent.intent_workflow_id}
              variant={variant}
            />
          )
        }
        noWrap={false}
      />
    </LineWrapper>
  );
};

const SentimentAnalysisEventLine = () => {
  const { t } = useTranslation('intent-conversation-analytics');
  return (
    <LineWrapper>
      <Circle />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 0.5,
        }}
      >
        <Typography variant='font12Medium'>
          {t('chatEventsSection.userSentimentIdentifiedAs')}
        </Typography>
        <Badge
          label={
            <Typography variant='font11'>
              {t('chatEventsSection.negative')}
            </Typography>
          }
          variant='error'
        />
      </Box>
    </LineWrapper>
  );
};

const GotoWorkflowEventLine = ({
  transcriptComponent,
  variant,
}: {
  transcriptComponent: GotoWorkflowTranscriptComponent;
  variant?: ABTestVariantInfo;
}) => {
  return (
    <LineWrapper>
      <Circle />
      <IntentBadge
        intentDefinitionId={transcriptComponent.intent_id}
        intentName={transcriptComponent.intent_title}
        intentNameSuffix={
          variant && (
            <ClickableVariantPill
              intentWorkflowId={transcriptComponent.intent_workflow_id}
              variant={variant}
            />
          )
        }
        noWrap={false}
        // Reroutes into autoflow building blocks resolve to snapshot intents
        // that /intents never returns, so these can't be linked. Show the
        // building-block glyph rather than the red error dot.
        showBuildingBlockIconWhenUnresolved
      />
    </LineWrapper>
  );
};

const ArticleLineFromRelatedArticles = ({
  transcriptComponent,
}: {
  transcriptComponent: RelatedArticlesTranscriptComponent;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {transcriptComponent.articles.map(article => {
        return (
          <Box
            key={article.document_id}
            sx={{
              display: 'flex',
              gap: '4px',
              ml: '40px',
            }}
          >
            <Typography variant='font12'>
              <QuickFeedbackArticleLink
                href={article.link}
                rel='noreferrer'
                target='_blank'
              >
                {article.title}
              </QuickFeedbackArticleLink>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const ArticleLineFromArticleCard = ({
  transcriptComponent,
}: {
  transcriptComponent: ArticleCardTranscriptComponent;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '4px',
        ml: '40px',
      }}
    >
      <Typography variant='font12'>
        <QuickFeedbackArticleLink
          href={transcriptComponent.link}
          rel='noreferrer'
          target='_blank'
        >
          {transcriptComponent.title}
        </QuickFeedbackArticleLink>
      </Typography>
    </Box>
  );
};

const ExecutionEventLine = ({
  transcriptComponent,
}: {
  transcriptComponent: ExecutionTranscriptComponent;
}) => {
  const isHandoff = /^Start.*handoff$/.test(transcriptComponent.message);

  if (!isHandoff) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '4px',
        ml: '40px',
      }}
    >
      <Typography variant='font12'>{transcriptComponent.message}</Typography>
    </Box>
  );
};

const GenericEventLine = ({ message }: { message: string }) => {
  return (
    <Box
      sx={{
        ml: '40px',
      }}
    >
      <Typography variant='font12'>{message}</Typography>
    </Box>
  );
};

const ABTestVariantAssignmentEventLine = ({
  transcriptComponent,
  variant,
}: {
  transcriptComponent: SolveABTestVariantAssignmentTranscriptComponent;
  variant?: ABTestVariantInfo;
}) => {
  return (
    <LineWrapper>
      <Circle />
      <IntentBadge
        intentNameSuffix={
          variant && (
            <ClickableVariantPill
              intentWorkflowId={transcriptComponent.intent_workflow_id}
              variant={variant}
            />
          )
        }
        intentWorkflowId={transcriptComponent.intent_workflow_id}
        noWrap={false}
      />
    </LineWrapper>
  );
};

const ClickableVariantPill = ({
  intentWorkflowId,
  variant,
}: {
  intentWorkflowId?: string;
  variant: ABTestVariantInfo;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!intentWorkflowId) {
      return;
    }
    openTab(
      getWorkflowBuilderUrl({
        intentWorkflowId,
        version: variant.intent_workflow_version,
        versionType: 'published',
      }),
    );
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: intentWorkflowId ? 'pointer' : 'default',
      }}
    >
      <VariantPill variant={variant} />
    </Box>
  );
};

const Circle = styled('div')`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1px solid white;
  background-color: black;
`;
const LineWrapper = styled('div')`
  margin-left: -5px;
  display: flex;
  gap: 20px;
  align-items: center;
`;

const VerticalLine = styled('div')`
  width: 2px;
  background-color: black;
  margin-top: 8px;
  margin-bottom: 10px;
`;

const QuickFeedbackArticleLink = styled('a')`
  color: ${({ theme }) => theme.palette.colors.blue[600]};
`;
